import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Loading,
  PolicyRuleAlerts,
  policyRuleDisplayMessagePlacements,
  refundMethods,
  theme,
} from '@reveni-io/reveni-ui'

import ConsentModal from '../components/ConsentModal/ConsentModal'
import NotAllowTransactionModal from '../components/NotAllowTransactionModal/NotAllowTransactionModal'
import PolicyRuleMessageWrapper from '../components/PolicyRuleMessageWrapper/PolicyRuleMessageWrapper'
import RequestNotAllowedModal from '../components/RequestNotAllowedModal/RequestNotAllowedModal'
import ReturnGroupReminder from '../components/ReturnGroupReminder/ReturnGroupReminder'
import Summary from '../components/Summary/Summary'
import AppContext from '../context/AppContext'
import { needsRefund } from '../helpers'
import { navigateTo } from '../helpers/location/location'
import useBankAccount from '../hooks/useBankAccount/useBankAccount'
import useCancellationsPortal from '../hooks/useCancellationsPortal/useCancellationsPortal'
import useCompleteReturn from '../hooks/useCompleteReturn/useCompleteReturn'
import useConsentModal from '../hooks/useConsentModal/useConsentModal'
import useTransactionTypes from '../hooks/useTransactionTypes/useTransactionTypes'
import { isOriginalPaymentMethodAvailable } from '../RefundSelectionPage/helpers'
import useSendRefundMethod from './../hooks/useSendRefundMethod/useSendRefundMethod'
import BankAccountBlackListModal from './BankAccountBlackListModal/BankAccountBlackListModal'
import CompleteProcessButton from './CompleteProcessButton/CompleteProcessButton'
import useRefundType from './hooks/useRefundType/useRefundType'
import SummaryItems from './SummaryItems/SummaryItems'
import SummaryMessage from './SummaryMessage/SummaryMessage'
import { onCompleteErrors } from './constants'
import * as Styles from './styles'

const SummaryPage = () => {
  const { context } = useContext(AppContext)
  const navigate = useNavigate()
  const { getRefundType } = useRefundType()
  const [showBlackListModal, setShowBlackListModal] = useState(false)
  const [showNotAllowTransactionModal, setShowNotAllowTransactionModal] = useState(false)
  const { bankAccount } = useBankAccount()
  const { getOrderTransactionType } = useTransactionTypes()
  const { isCancellationsPortal } = useCancellationsPortal()
  const { consentModal, showConsentModal, askForConsent, confirmConsent, closeConsentModal } = useConsentModal(
    policyRuleDisplayMessagePlacements.finalConfirmationPage
  )

  const { sendRefundMethod, loading: fetchingReturnInfo } = useSendRefundMethod(
    context.orderInfo.id,
    context.orderInfo.returnId,
    getRefundType()
  )
  const [showRequestNotAllowedModal, setShowRequestNotAllowedModal] = useState(false)
  const isRefundNeeded = needsRefund(context?.orderAmount?.summary_total_amount)

  const handleDeniedOnComplete = () => {
    if (isOriginalPaymentMethodAvailable(context.orderInfo.allowed_refund_methods)) {
      sendRefundMethod({
        orderId: context.orderInfo.id,
        returnId: context.orderInfo.returnId,
        refundMethod: refundMethods.originalPaymentMethod,
      })
      setShowBlackListModal(true)
    } else {
      setShowNotAllowTransactionModal(true)
    }
  }

  const handleForbiddenOnComplete = code => {
    if (code === onCompleteErrors.denied) {
      handleDeniedOnComplete()
    } else if (code === onCompleteErrors.noDeliveryMethod) {
      navigate(`/${context?.merchantInfo?.slug}`)
    } else {
      setShowRequestNotAllowedModal(true)
    }
  }

  const { completeReturn, completingReturn, completingReturnMessage } = useCompleteReturn({ handleForbiddenOnComplete })

  const onComplete = refundType => {
    if (askForConsent()) return

    if (refundType === refundMethods.bankTransfer) {
      completeReturn(bankAccount?.id, refundType)
    } else {
      completeReturn(null, refundType)
    }
  }

  const onAcceptBlackListModal = () => {
    completeReturn(null, refundMethods.originalPaymentMethod)
  }

  const handleAcceptNotAllowTransaction = () => {
    navigateTo(context.orderInfo?.reject_url)
  }

  return (
    <Loading show={fetchingReturnInfo} height={150}>
      <PolicyRuleMessageWrapper>
        <PolicyRuleAlerts
          displayMessages={context.displayMessages}
          placement={policyRuleDisplayMessagePlacements.finalConfirmationPage}
          includeGlobalMessages
        />
      </PolicyRuleMessageWrapper>
      <SummaryItems summaryLineItems={context.summaryLineItems} isCancellationsPortal={isCancellationsPortal} />
      <ReturnGroupReminder returnGroup={context?.orderInfo?.return_group} paddingTop={theme.whiteLabel.spacing.sp24} />
      <Styles.SummaryContainer>
        <Summary showReminder={false} showDistinctCurrencyReminder />
      </Styles.SummaryContainer>
      <SummaryMessage
        isInstantExchange={context?.orderInfo?.is_instant_exchange_allowed}
        orderAmount={context?.orderAmount}
        needsRefund={isRefundNeeded}
        refundType={getRefundType()}
        transactionType={getOrderTransactionType()}
        issueRefundAtStatus={context?.orderInfo?.issue_refund_at_status}
        isCancellationsPortal={isCancellationsPortal}
      />
      <Styles.ActionsContainer>
        <CompleteProcessButton
          refundType={getRefundType()}
          completingReturn={completingReturn}
          onComplete={onComplete}
          completingReturnMessage={completingReturnMessage}
        />
      </Styles.ActionsContainer>
      <BankAccountBlackListModal
        show={showBlackListModal}
        onClose={() => setShowBlackListModal(false)}
        onAccept={onAcceptBlackListModal}
      />
      <NotAllowTransactionModal
        show={showNotAllowTransactionModal}
        onClose={() => setShowNotAllowTransactionModal(false)}
        onAccept={handleAcceptNotAllowTransaction}
      />
      <RequestNotAllowedModal show={showRequestNotAllowedModal} merchantSlug={context?.merchantInfo?.slug} />
      <ConsentModal
        show={showConsentModal}
        consentModal={consentModal}
        onConfirm={confirmConsent}
        onClose={closeConsentModal}
      />
    </Loading>
  )
}

export default SummaryPage
